import type { CSSProperties } from "react";
import {
  FP_CONTAINER_ATTRIBUTES,
  type FpContainerAttribute,
} from "@forgepoint/shared";

/**
 * Shared container props for the fp-* vocabulary. Every primitive accepts them,
 * and each value is a raw CSS value. The attribute vocabulary lives in
 * `@forgepoint/shared` so the sanitizer allowlist and this renderer can never
 * drift apart.
 */

/** The container attribute values read off one element. */
export type FpContainerValues = Partial<Record<FpContainerAttribute, string>>;

/**
 * Reads the container attributes off a DOM element.
 * @param element - The fp-* element to read
 * @returns The attribute values that were present
 */
export function readContainerValues(element: Element): FpContainerValues {
  return FP_CONTAINER_ATTRIBUTES.reduce<FpContainerValues>((values, name) => {
    const value = element.getAttribute(name);
    return value === null ? values : { ...values, [name]: value };
  }, {});
}

/**
 * Turns the container attributes into an inline style object.
 * @param values - The container attribute values read off the element
 * @returns The style object, empty when no container attribute was set
 */
export function containerStyle(values: FpContainerValues): CSSProperties {
  return {
    ...styleFor("padding", values.padding),
    ...styleFor("margin", values.margin),
    ...styleFor("backgroundColor", values.background),
    ...styleFor("border", values.border),
    ...styleFor("borderTop", values["border-top"]),
    ...styleFor("borderRight", values["border-right"]),
    ...styleFor("borderBottom", values["border-bottom"]),
    ...styleFor("borderLeft", values["border-left"]),
    ...styleFor("borderRadius", values.radius),
    ...styleFor("width", values.width),
    ...styleFor("height", values.height),
    ...styleFor("minHeight", values["min-height"]),
    ...styleFor("maxHeight", values["max-height"]),
    ...styleFor("maxWidth", values["max-width"]),
    ...styleFor("minWidth", values["min-width"]),
    ...alignStyle(values.align, values.width),
    ...styleFor("gap", values.gap),
    ...styleFor("fontSize", values.size),
    ...styleFor("color", values.color),
    ...styleFor("fontFamily", values.font),
    ...styleFor("fontWeight", values.weight),
    ...styleFor("fontStyle", values["font-style"]),
    ...styleFor("textDecoration", values.decoration),
    ...styleFor("textTransform", values.transform),
    ...styleFor("letterSpacing", values.tracking),
  };
}

/**
 * The container style for a primitive that gives `width` and `height` their own
 * meaning, so neither reaches the container box.
 * @param values - The container attribute values read off the element
 * @returns The style object, without width or height
 */
export function containerStyleWithoutSize(
  values: FpContainerValues,
): CSSProperties {
  return containerStyle({ ...values, width: undefined, height: undefined });
}

/**
 * Resolves a `width` or `height` value for a primitive that sizes itself rather
 * than its container box. A bare positive number means pixels, anything else is
 * a raw CSS length such as `180px` or `50%`.
 * @param value - The raw attribute value, or null when absent
 * @returns The CSS dimension, or null when absent, blank or a non-positive number
 */
export function resolveDimensionValue(value: string | null): string | null {
  if (value === null) {
    return null;
  }
  const trimmed = value.trim();
  if (trimmed === "") {
    return null;
  }
  const pixels = Number(trimmed);
  if (!Number.isNaN(pixels)) {
    return Number.isFinite(pixels) && pixels > 0 ? `${pixels}px` : null;
  }
  return trimmed;
}

/**
 * A one-property style fragment, or nothing when the attribute was absent.
 * @param property - The CSS property to set
 * @param value - The raw attribute value, or undefined when absent
 * @returns The style fragment
 */
function styleFor(
  property: keyof CSSProperties,
  value: string | undefined,
): CSSProperties {
  if (value === undefined) {
    return {};
  }
  return { [property]: value.trim() };
}

/**
 * The alignment style: `text-align` always, plus auto inline margins when the
 * block carries an explicit width, so a fixed-width block such as a totals box
 * can sit left, centred or right inside its parent.
 * @param align - The raw `align` attribute value, or undefined when absent
 * @param width - The raw `width` attribute value, or undefined when absent
 * @returns The style fragment
 */
function alignStyle(
  align: string | undefined,
  width: string | undefined,
): CSSProperties {
  if (align === undefined) {
    return {};
  }
  const textAlign = styleFor("textAlign", align);
  if (width === undefined) {
    return textAlign;
  }
  return { ...textAlign, ...alignMargins(align.trim()) };
}

/**
 * The auto inline margins pushing a fixed-width block to one side.
 * @param align - The trimmed `align` attribute value
 * @returns The margin fragment, empty for an unrecognized alignment
 */
function alignMargins(align: string): CSSProperties {
  if (align === "left") {
    return { marginRight: "auto" };
  }
  if (align === "center") {
    return { marginLeft: "auto", marginRight: "auto" };
  }
  if (align === "right") {
    return { marginLeft: "auto" };
  }
  return {};
}
